import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useFetchAuthorisation, useFetchUserData } from '../../utils/AppFunctions'
import notify from '../../utils/IANotification';
import AdminLayout from '../../layouts/AdminLayout'
import Switch from 'react-switch'
import { MdAdd, MdArrowBack, MdSave, MdDelete } from 'react-icons/md';
import {
  fetch_breakfast_by_id,
  create_new_breakfast,
  update_breakfast_by_id,
  delete_breakfast_by_id
} from "../../api/cafeAPI";
import SubNavbar from '../../components/ui/SubNavbar';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';

export default function AdminCafeFormPage() {
  const navigate = useNavigate()
  useFetchAuthorisation("user")
  useFetchUserData()

  let breakfast_id = new URLSearchParams(window.location.search).get("breakfast_id")

  const [activeTab, setActiveTab] = useState("general")
  const [breakfastData, setBreakfastData] = useState({
    name: "",
    is_selling: "1",
    menu: [],
    is_published: "0",
    datetime: new Date().toISOString().slice(0, 16),
  })

  const tabs = [
    { id: 'general', label: 'General Info & Date' },
    { id: 'menu', label: 'Cafe Menu Items' },
  ]

  const fetchBreakfastInformationById = async () => {
    if (!breakfast_id || breakfast_id === "new") return;
    let data = await fetch_breakfast_by_id(breakfast_id);
    if (data?.status === 200) {
      setBreakfastData({
        ...data.data,
        menu: data.data.menu || []
      });
    } else {
      notify({ type: "error", message: data?.message });
    }
  }

  useEffect(() => {
    fetchBreakfastInformationById()
  }, [])

  const createBreakfast = async () => {
    let data = await create_new_breakfast(breakfastData);
    if (data?.status === 200) {
      notify({ type: "success", message: "Cafe session created successfully" });
      navigate("/admin/cafe/details?breakfast_id=" + data.data.id);
    } else {
      notify({ type: "error", message: data?.message });
    }
  }

  const updateBreakfast = async () => {
    if (!breakfast_id || breakfast_id === "new") return;
    let data = await update_breakfast_by_id(breakfast_id, breakfastData);
    if (data?.status === 200) {
      notify({ type: "success", message: "Cafe session updated successfully" });
    } else {
      notify({ type: "error", message: data?.message });
    }
  }

  const deleteEvent = async () => {
    if (!breakfast_id || breakfast_id === "new") return;
    let data = await delete_breakfast_by_id(breakfast_id);
    if (data?.status === 200) {
      notify({ type: "success", message: "Cafe session deleted successfully" });
      navigate("/admin/cafe");
    } else {
      notify({ type: "error", message: data?.message });
    }
  }

  return (
    <AdminLayout
      navSelected={"Cafe"}
      headerText={breakfast_id === "new" ? "New Cafe Session" : "Edit Cafe Session"}
      toolbar={
        <>
          <div className="flex items-center gap-3">
            <Button variant="outline" icon={MdArrowBack} onClick={() => navigate('/admin/cafe')}>
              Back
            </Button>
            <h1 className='text-xl font-bold text-gray-900'>
              {breakfast_id === "new" ? "Create Cafe Session" : breakfastData.name || "Edit Cafe Session"}
            </h1>
          </div>
          <div className="flex items-center gap-3">
            {breakfast_id !== "new" && (
              <Button variant="danger" icon={MdDelete} onClick={deleteEvent}>
                Delete
              </Button>
            )}
            <Button variant="primary" icon={MdSave} onClick={() => {
              if (breakfast_id === "new") createBreakfast()
              else updateBreakfast()
            }}>
              {breakfast_id === "new" ? "Create" : "Save Changes"}
            </Button>
          </div>
        </>
      }
    >
      <SubNavbar
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
      />

      {/* TAB 1: GENERAL INFO */}
      {activeTab === "general" && (
        <Card title="Cafe Session Details" subtitle="Group/Ministry host, status toggles, and event timing.">
          <div className="space-y-6">
            <div className="input-container">
              <label className='input-label font-semibold'>Host Name / Ministry Group</label>
              <input
                type="text"
                className="text-input"
                value={breakfastData.name || ""}
                placeholder='e.g. Youth Ministry Breakfast'
                onChange={(e) => setBreakfastData({ ...breakfastData, name: e.target.value })}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Cafe Open for Sales</label>
                  <p className="text-xs text-gray-500">Toggle active selling status.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {breakfastData.is_selling == "1" ? "Open" : "Closed"}
                  </span>
                  <Switch
                    onChange={(checked) => setBreakfastData({ ...breakfastData, is_selling: checked ? "1" : "0" })}
                    checked={breakfastData.is_selling == "1"}
                  />
                </div>
              </div>

              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-xl border border-gray-200">
                <div>
                  <label className="font-semibold text-sm text-gray-900">Publication Status</label>
                  <p className="text-xs text-gray-500">Publish session on mobile app.</p>
                </div>
                <div className="flex items-center gap-3">
                  <span className="text-xs font-semibold text-gray-700">
                    {breakfastData.is_published == "1" ? "Published" : "Draft"}
                  </span>
                  <Switch
                    onChange={(checked) => setBreakfastData({ ...breakfastData, is_published: checked ? "1" : "0" })}
                    checked={breakfastData.is_published == "1"}
                  />
                </div>
              </div>
            </div>

            <div className="input-container max-w-md">
              <label className='input-label font-semibold'>Session Date &amp; Time</label>
              <input
                type="datetime-local"
                className="text-input"
                value={breakfastData.datetime || ""}
                onChange={(e) => setBreakfastData({ ...breakfastData, datetime: e.target.value })}
              />
            </div>
          </div>
        </Card>
      )}

      {/* TAB 2: MENU ITEMS */}
      {activeTab === "menu" && (
        <Card
          title="Cafe Menu Items"
          subtitle="Configure food items, prices, and item descriptions."
          headerRight={
            <Button
              variant="secondary"
              size="sm"
              icon={MdAdd}
              onClick={() => {
                let temp = [...(breakfastData.menu || [])]
                temp.push({ name: "", price: 0, description: "" })
                setBreakfastData({ ...breakfastData, menu: temp })
              }}
            >
              Add Item
            </Button>
          }
        >
          {breakfastData?.menu?.length > 0 ? (
            <div className="space-y-4">
              {breakfastData.menu.map((menu, index) => (
                <div key={index} className="p-5 bg-gray-50 rounded-2xl border border-gray-200 space-y-4">
                  <div className="flex items-center justify-between border-b border-gray-200 pb-3">
                    <span className="font-bold text-xs text-gray-900">Menu Item #{index + 1}</span>
                    <Button
                      variant="danger"
                      size="sm"
                      icon={MdDelete}
                      onClick={() => {
                        let temp = [...breakfastData.menu]
                        temp.splice(index, 1)
                        setBreakfastData({ ...breakfastData, menu: temp })
                      }}
                    >
                      Delete
                    </Button>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="input-container md:col-span-2">
                      <label className='input-label font-semibold'>Item Name</label>
                      <input
                        type="text"
                        className="text-input"
                        value={menu.name || ""}
                        placeholder='e.g. Kaya Toast Set'
                        onChange={(e) => {
                          let temp = [...breakfastData.menu]
                          temp[index].name = e.target.value
                          setBreakfastData({ ...breakfastData, menu: temp })
                        }}
                      />
                    </div>

                    <div className="input-container">
                      <label className='input-label font-semibold'>Price ($ SGD)</label>
                      <input
                        type="text"
                        className="text-input"
                        value={menu.price || ""}
                        placeholder='e.g. 2.50'
                        onChange={(e) => {
                          let temp = [...breakfastData.menu]
                          temp[index].price = e.target.value
                          setBreakfastData({ ...breakfastData, menu: temp })
                        }}
                      />
                    </div>
                  </div>

                  <div className="input-container">
                    <label className='input-label font-semibold'>Item Description</label>
                    <textarea
                      className="textarea-input h-20"
                      value={menu.description || ""}
                      placeholder='e.g. Served with 2 soft-boiled eggs and hot coffee/tea...'
                      onChange={(e) => {
                        let temp = [...breakfastData.menu]
                        temp[index].description = e.target.value
                        setBreakfastData({ ...breakfastData, menu: temp })
                      }}
                    />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-xs text-gray-400 italic py-6 text-center">No menu items added yet.</p>
          )}
        </Card>
      )}
    </AdminLayout>
  )
}
